import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const TOKEN_KEY = 'daggpt_token';
const USER_KEY  = 'daggpt_user';

function decodeJWT(token) {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
}

/**
 * GoogleCallbackPage - /auth/google/callback
 * Express backend redirects here after successful Google OAuth.
 * URL format: /auth/google/callback?token=<jwt>&next=<path>
 */
export default function GoogleCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const token = searchParams.get('token');
    const next  = searchParams.get('next') || '/';

    if (!token) {
      setStatus('error');
      setTimeout(() => navigate('/?google_error=missing_token'), 2000);
      return;
    }

    (async () => {
      try {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem('daggpt_authenticated', 'true');

        // Always fetch fresh user data from the server - never trust JWT claims for display info
        const API = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';
        const meRes = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        let currentUser = null;
        if (meRes.ok) {
          const { user } = await meRes.json();
          currentUser = user;
          if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
        } else {
          // Fallback: store minimal identity from JWT (no name/avatar to avoid stale data)
          const payload = decodeJWT(token);
          if (!payload) throw new Error('invalid token');
          const minimalUser = {
            id:       payload.sub,
            email:    payload.email,
            role:     payload.role || 'user',
            is_admin: payload.isAdmin || false,
          };
          currentUser = minimalUser;
          localStorage.setItem(USER_KEY, JSON.stringify(minimalUser));
        }

        // Determine redirect path based on profile completion
        const isProfileComplete = currentUser && currentUser.email && currentUser.wallet_address;
        const finalRedirect = isProfileComplete ? '/workspace' : '/dashboard?section=profile';
        
        setStatus('success');
        setTimeout(() => navigate(finalRedirect, { replace: true }), 500);
      } catch (err) {
        console.error('[DAGGPT] Google callback error:', err);
        setStatus('error');
        setTimeout(() => navigate('/?google_error=parse_failed'), 2000);
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #0f0f1a 50%, #0a0a0f 100%)',
    }}>
      <div style={{ textAlign: 'center', color: '#e2e8f0' }}>
        {status === 'loading' && (
          <>
            <div style={{
              width: '48px', height: '48px', margin: '0 auto 20px',
              border: '3px solid rgba(139,92,246,0.3)',
              borderTopColor: '#8b5cf6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ fontSize: '15px', color: '#94a3b8' }}>Signing you in with Google…</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <p style={{ fontSize: '15px', color: '#10b981' }}>Signed in! Redirecting…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
            <p style={{ fontSize: '15px', color: '#ef4444' }}>Something went wrong. Redirecting…</p>
          </>
        )}
      </div>
    </div>
  );
}
